import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { useUpdateWebhookMutation } from "@/pages/Private/redux/webhook/webhook.api";
import {
	UpdateWebhook,
	UpdateWebhookSchema,
	Webhook,
} from "@/pages/Private/pages/Company/pages/WebhookOverview/schema/webhook";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";
import { ButtonSize } from "@/components/Button/types";
import { Button } from "@/components/Button/Button";

import { buildWebhookUpdate } from "../utils/buildWebhookUpdate";
import { WebhookUrlField } from "./WebhookUrlField";
import { WebhookPayloadVersionField } from "./WebhookPayloadVersionField";
import { WebhookSigningSection } from "./signing/WebhookSigningSection";
import { WebhookSigningActions } from "./signing/useWebhookSigningDialogs";

interface updateWebhookModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	webhook: Webhook;
	/** Signing actions (PREC-4249); without them the dialog has no Signing section. */
	signingActions?: WebhookSigningActions;
}

export const UpdateWebhookModal: FunctionComponent<updateWebhookModalProps> = ({
	isOpen,
	setIsOpen,
	webhook,
	signingActions,
}) => {
	const { t } = useTranslation();
	const [updateWebhook, { isLoading, error }] = useUpdateWebhookMutation();

	const [customColumn, setCustomColumn] = useState<string>("");
	// The stored URL never reaches the browser; it is only sent when the user replaced it (PREC-4253).
	const [isReplacingUrl, setIsReplacingUrl] = useState<boolean>(false);

	const [currentFormState, setCurrentFormState] = useState<UpdateWebhook>({
		...webhook,
	});

	const handleSaveFormState = async (
		data: {
			value: UpdateWebhook[keyof UpdateWebhook];
			name: keyof UpdateWebhook;
		}[]
	) => {
		const newState = data.reduce(
			(acc, value) => {
				return {
					...acc,
					[value.name]: value.value,
				};
			},
			{ ...currentFormState }
		);

		setCurrentFormState({
			...newState,
		});

		reset(newState);
	};
	const isDuplicate = useMemo(() => {
		return (
			currentFormState.customColumns?.includes(customColumn) ||
			currentFormState.columnTemplates?.map((c) => c.name)?.includes(customColumn)
		);
	}, [customColumn, currentFormState]);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UpdateWebhook>({
		defaultValues: {
			...webhook,
			customColumns: [],
			removeCustomColumnIds: [],
		},
		resolver: zodResolver(UpdateWebhookSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: UpdateWebhook) => {
		try {
			await updateWebhook(buildWebhookUpdate(values, isReplacingUrl)).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={"Edit Webhook"}
		>
			<div>
				<div className="flex items-center mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							handleSaveFormState([{ value: e.target.value, name: "name" }]);
						}}
						label={"Name"}
						name={"name"}
						placeholder={"e.g. My Webhook"}
						value={currentFormState.name}
					/>
				</div>

				<WebhookUrlField
					error={!!errors.url?.message}
					isReplacing={isReplacingUrl}
					shownUrl={webhook.url}
					urlMasked={webhook.urlMasked}
					value={currentFormState.url ?? ""}
					onChange={(value) => handleSaveFormState([{ value, name: "url" }])}
					onKeep={() => {
						setIsReplacingUrl(false);
						handleSaveFormState([{ value: webhook.url, name: "url" }]);
					}}
					onReplace={() => {
						setIsReplacingUrl(true);
						handleSaveFormState([{ value: "", name: "url" }]);
					}}
				/>

				<WebhookPayloadVersionField
					// A webhook the API reports without a version is on the legacy array (PREC-4251).
					value={currentFormState.payloadVersion === 2 ? 2 : 1}
					onChange={(version) => handleSaveFormState([{ value: version, name: "payloadVersion" }])}
				/>

				<div className="flex items-center gap-2 mb-4">
					<Toggle
						isChecked={currentFormState.receivesData}
						name={"receivesData"}
						onChange={() => {
							handleSaveFormState([
								{ value: !currentFormState.receivesData, name: "receivesData" },
							]);
						}}
					/>
					<div className="text-sm text-text-meta">Receives data after workflow is completed</div>
				</div>

				{signingActions && <WebhookSigningSection actions={signingActions} webhook={webhook} />}

				{currentFormState.receivesData && (
					<>
						<div className="text-sm text-text-meta mb-4">
							When the workflow that is connected to this webhook will write back to signalist you
							need to define into which columns you want to write back
						</div>
						<div className="flex flex-row items-start space-y-4">
							<div className="text-ssm font-medium !mb-[-12px] grow">
								<InputField
									className="!mb-0 grow mr-4"
									error={!!errors.customColumns}
									handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
										setCustomColumn(event.target.value);
									}}
									name={"customColumns"}
									placeholder="Enter Column Name"
									value={customColumn ?? ""}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											if (customColumn && !isDuplicate) {
												handleSaveFormState([
													{
														value: [...(currentFormState.customColumns ?? []), customColumn],
														name: "customColumns",
													},
												]);
											}

											setCustomColumn("");
										}
									}}
								/>
							</div>
							<div className="!mt-0 ml-4 flex items-start">
								<Button
									className="!mt-0"
									disabled={!customColumn || isDuplicate}
									image={<Icon className="mr-2" icon={faPlus} />}
									size={ButtonSize.L}
									title="Add Column"
									onClick={() => {
										if (customColumn && !currentFormState.customColumns?.includes(customColumn)) {
											handleSaveFormState([
												{
													value: [...(currentFormState.customColumns ?? []), customColumn],
													name: "customColumns",
												},
											]);
											setCustomColumn("");
										}
									}}
								/>
							</div>
						</div>
						{customColumn && isDuplicate ? (
							<div className="text-xs text-text-meta mb-2 mt-1">Column names need to be unique</div>
						) : (
							""
						)}
						<div>
							<div className="text-ssm font-medium mt-4 mb-2">Column names</div>

							{(currentFormState?.columnTemplates ?? []).map((value, index) => (
								<div
									key={index}
									className="flex items-center justify-between h-[54px] px-2.5 py-4.5 rounded-xl border border-gray-200 mb-4"
								>
									<div>{value.name}</div>
									<button
										className="cursor-pointer"
										onClick={() => {
											const values = [...(currentFormState.columnTemplates ?? [])];

											values.splice(index, 1);
											handleSaveFormState([
												{
													value: values,
													name: "columnTemplates",
												},
												{
													value: [...(currentFormState.removeCustomColumnIds ?? []), value.id ?? 0],
													name: "removeCustomColumnIds",
												},
											]);
										}}
									>
										<img alt="trash" src={trashIcon} />
									</button>
								</div>
							))}

							{(currentFormState?.customColumns ?? []).map((value, index) => (
								<div
									key={index}
									className="flex items-center justify-between h-[54px] px-2.5 py-4.5 rounded-xl border border-gray-200 mb-4"
								>
									<div>{value}</div>
									<button
										className="cursor-pointer"
										onClick={() => {
											const values = [...(currentFormState.customColumns ?? [])];

											values.splice(index, 1);
											handleSaveFormState([
												{
													value: values,
													name: "customColumns",
												},
											]);
										}}
									>
										<img alt="trash" src={trashIcon} />
									</button>
								</div>
							))}
						</div>
					</>
				)}

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
		</Modal>
	);
};
